import { useCallback } from "react";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import { useUniqueId } from "../Utils/uniqueId";
import OptionInput from "./OptionInput";
import BudgetAmount from "../Form/Field/Budget/BudgetAmount";
import { useFormatMessage } from "../Intl/Context";

const INPUT_TYPE_CHECKBOX = "checkbox";
const INPUT_TYPE_RADIO = "radio";

const BudgetLineCheckboxInput = props => {
  const { budgetLine, currentOptionIds = [], onValueChange, className, unit } = props;
  const { budgetLineOptions = [], maximumSelectableOptions } = budgetLine;

  const checkboxGroupName = useUniqueId("budget-line-checkbox-");

  // Use radio buttons when max selectable options is 1
  const useRadioButtons = maximumSelectableOptions === 1;
  const inputType = useRadioButtons ? INPUT_TYPE_RADIO : INPUT_TYPE_CHECKBOX;

  const t = useFormatMessage();
  const selectOptionsLabel = t(
    useRadioButtons ? intlMessages.selectOneOption : intlMessages.selectOptions
  );
  const maxSelectionsLabel = t(intlMessages.maxSelections, {
    max: maximumSelectableOptions
  });

  const handleOptionChange = useCallback(
    event => {
      const { value: optionId, checked: isChecked } = event.target;

      let newSelectedIds;

      if (useRadioButtons) {
        // For radio buttons, always replace with the new selection
        newSelectedIds = [optionId];
      } else {
        // For checkboxes, add or remove from array
        newSelectedIds = [...currentOptionIds];

        if (isChecked) {
          // Check if we've reached the maximum
          if (
            maximumSelectableOptions &&
            newSelectedIds.length >= maximumSelectableOptions
          ) {
            // Don't add, we're at the limit
            event.preventDefault();
            return;
          }
          newSelectedIds.push(optionId);
        } else {
          newSelectedIds = newSelectedIds.filter(id => id !== optionId);
        }
      }

      // Find all selected options
      const selectedOptions = budgetLineOptions.filter(option =>
        newSelectedIds.includes(option.id)
      );

      if (onValueChange) {
        onValueChange(selectedOptions);
      }
    },
    [
      budgetLineOptions,
      currentOptionIds,
      maximumSelectableOptions,
      onValueChange,
      useRadioButtons
    ]
  );

  const maximumOptionsSelected =
    !useRadioButtons &&
    maximumSelectableOptions &&
    currentOptionIds.length >= maximumSelectableOptions;

  // If there are no options, don't render anything
  if (budgetLineOptions.length === 0) return null;

  const checkboxClassName = classNames(
    {
      "budget-line-checkbox-input": !useRadioButtons,
      "budget-line-radio-input": useRadioButtons
    },
    className
  );

  return (
    <div className={checkboxClassName}>
      <p className="budget-line-checkbox-description text-small">
        {selectOptionsLabel}

        {maximumSelectableOptions && maximumSelectableOptions > 1 && (
          <span className="text-muted margin-left-xxs">{maxSelectionsLabel}</span>
        )}
      </p>

      <div className="budget-line-checkbox-options">
        {budgetLineOptions.map(option => {
          const isChecked = currentOptionIds.includes(option.id);
          const optionId = `${checkboxGroupName}-${option.position}`;
          const isDisabled = !isChecked && maximumOptionsSelected;

          return (
            <OptionInput
              key={option.id}
              type={inputType}
              option={option}
              value={option.id}
              id={optionId}
              name={checkboxGroupName}
              onChange={handleOptionChange}
              checked={isChecked}
              disabled={isDisabled}
              className="form-check-input"
            >
              <span className="d-flex justify-content-between w-100">
                <span className="budget-line-checkbox-label">{option.label}</span>

                <span className="budget-line-checkbox-value">
                  <BudgetAmount amount={option.value} unit={unit} />
                </span>
              </span>
            </OptionInput>
          );
        })}
      </div>
    </div>
  );
};

export default BudgetLineCheckboxInput;

const intlMessages = defineMessages({
  selectOptions: {
    id: "input.budgetLineCheckbox.selectOptions",
    defaultMessage: "Sélectionnez une ou plusieurs options :"
  },
  selectOneOption: {
    id: "input.budgetLineCheckbox.selectOneOption",
    defaultMessage: "Sélectionnez une option :"
  },
  maxSelections: {
    id: "input.budgetLineCheckbox.maxSelections",
    defaultMessage: "{max} options maximum"
  }
});
